import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { getDefaultStore } from 'jotai'
import { sessionAtom, sessionExpiredAtom } from '@/store/auth.atom'
import { withRefresh } from './refresh'

declare module 'axios' {
    interface InternalAxiosRequestConfig {
        _retry?: boolean
    }
}

function createInstance(extra: AxiosRequestConfig = {}): AxiosInstance {
    const instance = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30_000,
        headers: { 'Content-Type': 'application/json' },
        ...extra,
    })

    // ---- Request: attach access token + locale headers ----
    instance.interceptors.request.use((req: InternalAxiosRequestConfig) => {
        const session = getDefaultStore().get(sessionAtom)
        if (session?.accessToken) {
            req.headers.Authorization = `Bearer ${session.accessToken}`
        }
        req.headers.TimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (typeof navigator !== 'undefined' && navigator.language) {
            req.headers['Accept-Language'] = navigator.language
        }
        return req
    })

    // ---- Response: refresh-and-retry on 401 (or wrapped 400) ----
    instance.interceptors.response.use(
        (res) => res,
        async (error) => {
            const original = error.config as InternalAxiosRequestConfig | undefined
            const status = error.response?.status
            const data = error.response?.data

            const isTokenError =
                status === 401 ||
                status === 403 ||
                (status === 400 &&
                    typeof data?.responseMessage === 'string' &&
                    (data.responseMessage.includes('401') ||
                        data.responseMessage.includes('403') ||
                        data.responseMessage.includes('expired')));

            if (!isTokenError || !original || original._retry) {
                throw error
            }

            const store = getDefaultStore()
            if (store.get(sessionExpiredAtom)) {
                throw error
            }
            original._retry = true

            try {
                return await withRefresh(() => instance(original))
            } catch (refreshError) {
                if (!store.get(sessionExpiredAtom)) {
                    store.set(sessionExpiredAtom, true)
                }
                store.set(sessionAtom, null)
                throw refreshError
            }
        },
    )

    return instance
}

/** JSON API instance — used by every `*.api.ts` module. */
export const api = createInstance()

/** Re-export so other modules can `createInstance` if they need a third variant. */
export { createInstance }
